import { FaStickyNote, FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaFilter, FaSort, FaTag, FaCalendarDay, FaCheckCircle, FaClock, FaExclamationTriangle } from "react-icons/fa";
import { useState } from "react";
import { useAuth } from "./AuthContext";

export default function CommitteeNotes() {
  const { user } = useAuth();
  const isAdmin = user?.isProfileAdmin || false;

  const committees = [
    "All Committees",
    "Steering Committee",
    "Chair",
    "Vice Chair",
    "Academic Staff",
    "Researchers",
    "Communications",
    "HR",
    "Belonging",
    "OSG",
    "Governance Liaison Officer",
  ];

  const updateTypes = [
    "General Update",
    "Action Item",
    "Decision",
    "Announcement",
    "Discussion Point",
    "Follow-up Required",
    "Completed Task",
  ];

  const statusOptions = [
    "In Progress",
    "Completed",
    "Pending",
    "Blocked",
    "On Hold",
  ];

  const noteTemplates = {
    "General Update": "Update:\n\nContext:\n\nNext Steps:",
    "Action Item": "Action Required:\n\nAssigned To:\n\nDeadline:\n\nStatus:",
    "Decision": "Decision Made:\n\nRationale:\n\nImpact:\n\nImplementation Date:",
    "Announcement": "Announcement:\n\nAudience:\n\nDeadline/Date:\n\nAction Required:",
    "Discussion Point": "Topic:\n\nKey Points:\n\nQuestions Raised:\n\nNext Steps:",
    "Follow-up Required": "Item:\n\nFollow-up With:\n\nBy Date:\n\nReason:",
    "Completed Task": "Task Completed:\n\nOutcome:\n\nLessons Learned:\n\nNext Steps:",
  };

  const [notes, setNotes] = useState({
    "Steering Committee": [
      {
        id: 1,
        content: "Discussed budget allocation for Q1 2026. Need to review proposals from each committee by January 20th.",
        author: "Josie Simm",
        date: "2026-01-04",
        timestamp: new Date("2026-01-04T14:30:00").toISOString(),
        updateType: "Decision",
        status: "Pending",
        tags: ["Budget", "Q1 2026"],
        followUpDate: "2026-01-20",
      },
      {
        id: 2,
        content: "Planning staff appreciation event for February. Communications team to handle announcements.",
        author: "Mohammed Waseem Chaudry",
        date: "2026-01-03",
        timestamp: new Date("2026-01-03T10:15:00").toISOString(),
        updateType: "Action Item",
        status: "In Progress",
        tags: ["Events", "Communications"],
        followUpDate: "2026-02-01",
      },
    ],
    "Academic Staff": [
      {
        id: 1,
        content: "Survey results from academic staff indicate strong interest in professional development workshops. Will coordinate with HR.",
        author: "Karol Tolpa",
        date: "2026-01-04",
        timestamp: new Date("2026-01-04T11:00:00").toISOString(),
        updateType: "General Update",
        status: "In Progress",
        tags: ["Professional Development", "Survey"],
        followUpDate: "2026-01-15",
      },
    ],
    "Researchers": [
      {
        id: 1,
        content: "New lab safety guidelines need to be distributed. Working with relevant departments on implementation timeline.",
        author: "Anand Kant Das",
        date: "2026-01-03",
        timestamp: new Date("2026-01-03T15:45:00").toISOString(),
        updateType: "Action Item",
        status: "In Progress",
        tags: ["Safety", "Guidelines"],
        followUpDate: "2026-01-10",
      },
    ],
    "Communications": [
      {
        id: 1,
        content: "Newsletter scheduled for January 15th. Need input from all committees by January 10th.",
        author: "Eudora Okine",
        date: "2026-01-04",
        timestamp: new Date("2026-01-04T09:30:00").toISOString(),
        updateType: "Announcement",
        status: "Pending",
        tags: ["Newsletter", "Deadline"],
        followUpDate: "2026-01-10",
      },
    ],
    "HR": [
      {
        id: 1,
        content: "Updated onboarding process for new staff members approved. Implementation begins February 1st.",
        author: "Sabah Siddiqui",
        date: "2026-01-03",
        timestamp: new Date("2026-01-03T13:20:00").toISOString(),
        updateType: "Decision",
        status: "Completed",
        tags: ["Onboarding", "Process"],
        followUpDate: "2026-02-01",
      },
    ],
    "Belonging": [
      {
        id: 1,
        content: "Diversity and inclusion workshop scheduled for January 25th. RSVP link to be shared next week.",
        author: "Amnah Khan",
        date: "2026-01-04",
        timestamp: new Date("2026-01-04T16:00:00").toISOString(),
        updateType: "Announcement",
        status: "In Progress",
        tags: ["Workshop", "Diversity"],
        followUpDate: "2026-01-25",
      },
    ],
    "OSG": [],
    "Chair": [],
    "Vice Chair": [],
    "Governance Liaison Officer": [],
  });

  const [selectedCommittee, setSelectedCommittee] = useState("All Committees");
  const [showAddNote, setShowAddNote] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [noteContent, setNoteContent] = useState("");
  const [noteUpdateType, setNoteUpdateType] = useState("General Update");
  const [noteStatus, setNoteStatus] = useState("In Progress");
  const [noteTags, setNoteTags] = useState([]);
  const [noteFollowUpDate, setNoteFollowUpDate] = useState("");
  const [newTag, setNewTag] = useState("");
  
  // Filters and sorting
  const [filterType, setFilterType] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");
  const [filterTag, setFilterTag] = useState("All");
  const [sortBy, setSortBy] = useState("newest"); // newest, oldest, type, status

  const handleAddNote = () => {
    if (selectedCommittee === "All Committees") {
      alert("Please select a specific committee to add a note.");
      return;
    }
    setEditingNote(null);
    setNoteContent("");
    setNoteUpdateType("General Update");
    setNoteStatus("In Progress");
    setNoteTags([]);
    setNoteFollowUpDate("");
    setShowAddNote(true);
  };

  const handleEditNote = (note) => {
    setEditingNote(note);
    setNoteContent(note.content);
    setNoteUpdateType(note.updateType || "General Update");
    setNoteStatus(note.status || "In Progress");
    setNoteTags(note.tags || []);
    setNoteFollowUpDate(note.followUpDate || "");
    setShowAddNote(true);
  };

  const handleUseTemplate = (type) => {
    setNoteUpdateType(type);
    setNoteContent(noteTemplates[type]);
  };

  const handleAddTag = () => {
    if (newTag.trim() && !noteTags.includes(newTag.trim())) {
      setNoteTags([...noteTags, newTag.trim()]);
      setNewTag("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setNoteTags(noteTags.filter(tag => tag !== tagToRemove));
  };

  const handleDeleteNote = (noteId) => {
    if (window.confirm("Are you sure you want to delete this note?")) {
      setNotes({
        ...notes,
        [selectedCommittee]: notes[selectedCommittee].filter(n => n.id !== noteId),
      });
    }
  };

  const handleSaveNote = () => {
    if (!noteContent.trim()) {
      alert("Please enter note content");
      return;
    }

    if (editingNote) {
      // Update existing note
      setNotes({
        ...notes,
        [selectedCommittee]: notes[selectedCommittee].map(n =>
          n.id === editingNote.id
            ? { 
                ...n, 
                content: noteContent,
                updateType: noteUpdateType,
                status: noteStatus,
                tags: noteTags,
                followUpDate: noteFollowUpDate,
              }
            : n
        ),
      });
    } else {
      // Add new note (at the top)
      const newNote = {
        id: Math.max(0, ...notes[selectedCommittee].map(n => n.id)) + 1,
        content: noteContent,
        author: user?.name || "Unknown",
        date: new Date().toISOString().split('T')[0],
        timestamp: new Date().toISOString(),
        updateType: noteUpdateType,
        status: noteStatus,
        tags: noteTags,
        followUpDate: noteFollowUpDate,
      };
      setNotes({
        ...notes,
        [selectedCommittee]: [newNote, ...notes[selectedCommittee]],
      });
    }

    setShowAddNote(false);
    setNoteContent("");
    setNoteUpdateType("General Update");
    setNoteStatus("In Progress");
    setNoteTags([]);
    setNoteFollowUpDate("");
    setEditingNote(null);
  };

  // Get notes based on selected committee
  const getNotesForView = () => {
    if (selectedCommittee === "All Committees") {
      // Combine all notes from all committees with committee label
      const allNotes = [];
      Object.keys(notes).forEach(committee => {
        notes[committee].forEach(note => {
          allNotes.push({
            ...note,
            committee: committee,
          });
        });
      });
      return allNotes;
    } else {
      // Return notes for specific committee
      return (notes[selectedCommittee] || []).map(note => ({
        ...note,
        committee: selectedCommittee,
      }));
    }
  };

  const notesForView = getNotesForView();

  // Get all unique tags across displayed notes
  const allTags = [...new Set(notesForView.flatMap(note => note.tags || []))];

  // Filter and sort notes
  let filteredNotes = notesForView.filter(note => {
    if (filterType !== "All" && note.updateType !== filterType) return false;
    if (filterStatus !== "All" && note.status !== filterStatus) return false;
    if (filterTag !== "All" && !note.tags?.includes(filterTag)) return false;
    return true;
  });

  // Sort notes
  filteredNotes = [...filteredNotes].sort((a, b) => {
    switch (sortBy) {
      case "newest":
        return new Date(b.timestamp) - new Date(a.timestamp);
      case "oldest":
        return new Date(a.timestamp) - new Date(b.timestamp);
      case "type":
        return a.updateType.localeCompare(b.updateType);
      case "status":
        return a.status.localeCompare(b.status);
      default:
        return 0;
    }
  });

  const committeeNotes = filteredNotes;

  // Helper functions for colors
  const getTypeColor = (type) => {
    const colors = {
      "General Update": "#667eea",
      "Action Item": "#ed8936",
      "Decision": "#48bb78",
      "Announcement": "#9f7aea",
      "Discussion Point": "#4299e1",
      "Follow-up Required": "#f56565",
      "Completed Task": "#38b2ac",
    };
    return colors[type] || "#667eea";
  };

  const getStatusColor = (status) => {
    const colors = {
      "In Progress": "#4299e1",
      "Completed": "#48bb78",
      "Pending": "#ed8936",
      "Blocked": "#f56565",
      "On Hold": "#a0aec0",
    };
    return colors[status] || "#4299e1";
  };

  return (
    <div
      style={{
        padding: "40px",
        maxWidth: "1400px",
        margin: "0 auto",
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: "30px" }}>
        <h1
          style={{
            fontSize: "32px",
            fontWeight: "700",
            color: "#2d3748",
            marginBottom: "8px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <FaStickyNote style={{ color: "#667eea" }} />
          Committee Notes
        </h1>
        <p style={{ color: "#718096", fontSize: "16px" }}>
          Running notes and updates from each committee for easy reference during meetings
        </p>
      </div>

      {/* Committee Selector */}
      <div
        style={{
          background: "white",
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
          border: "1px solid #e2e8f0",
          marginBottom: "30px",
        }}
      >
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {committees.map((committee) => (
            <button
              key={committee}
              onClick={() => setSelectedCommittee(committee)}
              style={{
                padding: "10px 20px",
                background:
                  selectedCommittee === committee
                    ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                    : "white",
                color: selectedCommittee === committee ? "white" : "#4a5568",
                border: "1px solid",
                borderColor: selectedCommittee === committee ? "#667eea" : "#e2e8f0",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                if (selectedCommittee !== committee) {
                  e.currentTarget.style.background = "#f7fafc";
                }
              }}
              onMouseLeave={(e) => {
                if (selectedCommittee !== committee) {
                  e.currentTarget.style.background = "white";
                }
              }}
            >
              {committee}
              {(() => {
                let count = 0;
                if (committee === "All Committees") {
                  // Count all notes across all committees
                  Object.keys(notes).forEach(c => {
                    count += notes[c].length;
                  });
                } else if (notes[committee]) {
                  count = notes[committee].length;
                }
                
                return count > 0 ? (
                  <span
                    style={{
                      marginLeft: "8px",
                      padding: "2px 6px",
                      background: selectedCommittee === committee ? "rgba(255,255,255,0.3)" : "#e2e8f0",
                      borderRadius: "10px",
                      fontSize: "11px",
                    }}
                  >
                    {count}
                  </span>
                ) : null;
              })()}
            </button>
          ))}
        </div>
      </div>

      {/* Filters and Sort */}
      <div
        style={{
          background: "white",
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
          border: "1px solid #e2e8f0",
          marginBottom: "20px",
        }}
      >
        <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <FaFilter style={{ color: "#667eea" }} />
            <span style={{ fontSize: "14px", fontWeight: "600", color: "#2d3748" }}>Filters:</span>
          </div>
          
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            style={{
              padding: "8px 12px",
              border: "1px solid #e2e8f0",
              borderRadius: "6px",
              fontSize: "13px",
              cursor: "pointer",
            }}
          >
            <option value="All">All Types</option>
            {updateTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{
              padding: "8px 12px",
              border: "1px solid #e2e8f0",
              borderRadius: "6px",
              fontSize: "13px",
              cursor: "pointer",
            }}
          >
            <option value="All">All Statuses</option>
            {statusOptions.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>

          {allTags.length > 0 && (
            <select
              value={filterTag}
              onChange={(e) => setFilterTag(e.target.value)}
              style={{
                padding: "8px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                fontSize: "13px",
                cursor: "pointer",
              }}
            >
              <option value="All">All Tags</option>
              {allTags.map(tag => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>
          )}

          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "8px" }}>
            <FaSort style={{ color: "#667eea" }} />
            <span style={{ fontSize: "14px", fontWeight: "600", color: "#2d3748" }}>Sort:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: "8px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                fontSize: "13px",
                cursor: "pointer",
              }}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="type">By Type</option>
              <option value="status">By Status</option>
            </select>
          </div>
        </div>
      </div>

      {/* Notes Section */}
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <h2
            style={{
              fontSize: "24px",
              fontWeight: "600",
              color: "#2d3748",
              margin: 0,
            }}
          >
            {selectedCommittee === "All Committees" ? "All Notes" : `${selectedCommittee} Notes`}
            {committeeNotes.length !== notesForView.length && (
              <span style={{ fontSize: "16px", color: "#718096", fontWeight: "400" }}>
                {" "}({committeeNotes.length} of {notesForView.length})
              </span>
            )}
            {selectedCommittee === "All Committees" && committeeNotes.length === notesForView.length && (
              <span style={{ fontSize: "16px", color: "#718096", fontWeight: "400" }}>
                {" "}({committeeNotes.length} total)
              </span>
            )}
          </h2>
          <button
            onClick={handleAddNote}
            disabled={selectedCommittee === "All Committees"}
            style={{
              padding: "10px 20px",
              background: selectedCommittee === "All Committees" 
                ? "#cbd5e0" 
                : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: selectedCommittee === "All Committees" ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              transition: "transform 0.2s",
              opacity: selectedCommittee === "All Committees" ? 0.6 : 1,
            }}
            onMouseEnter={(e) => {
              if (selectedCommittee !== "All Committees") {
                e.currentTarget.style.transform = "scale(1.05)";
              }
            }}
            onMouseLeave={(e) => {
              if (selectedCommittee !== "All Committees") {
                e.currentTarget.style.transform = "scale(1)";
              }
            }}
            title={selectedCommittee === "All Committees" ? "Select a specific committee to add a note" : "Add a new note"}
          >
            <FaPlus /> Add Note
          </button>
        </div>

        {/* Notes List */}
        {committeeNotes.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {committeeNotes.map((note) => (
              <div
                key={note.id}
                style={{
                  background: "white",
                  borderRadius: "12px",
                  padding: "24px",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
                  border: "1px solid #e2e8f0",
                }}
              >
                {/* Header with author and metadata */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "12px" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px", flexWrap: "wrap" }}>
                      <div style={{ fontSize: "13px", color: "#667eea", fontWeight: "600" }}>
                        {note.author}
                      </div>
                      {selectedCommittee === "All Committees" && (
                        <span
                          style={{
                            padding: "4px 10px",
                            background: "#2d3748",
                            color: "white",
                            borderRadius: "6px",
                            fontSize: "11px",
                            fontWeight: "600",
                          }}
                        >
                          📁 {note.committee}
                        </span>
                      )}
                      <span
                        style={{
                          padding: "4px 10px",
                          background: getTypeColor(note.updateType),
                          color: "white",
                          borderRadius: "6px",
                          fontSize: "11px",
                          fontWeight: "600",
                        }}
                      >
                        {note.updateType}
                      </span>
                      <span
                        style={{
                          padding: "4px 10px",
                          background: getStatusColor(note.status),
                          color: "white",
                          borderRadius: "6px",
                          fontSize: "11px",
                          fontWeight: "600",
                        }}
                      >
                        {note.status}
                      </span>
                    </div>
                    <div style={{ fontSize: "12px", color: "#a0aec0", display: "flex", alignItems: "center", gap: "12px" }}>
                      <span>
                        {new Date(note.timestamp).toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                      {note.followUpDate && (
                        <span style={{ display: "flex", alignItems: "center", gap: "4px", color: "#ed8936" }}>
                          <FaCalendarDay size={12} />
                          Follow-up: {new Date(note.followUpDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      )}
                    </div>
                  </div>
                  {isAdmin && (
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        onClick={() => handleEditNote(note)}
                        style={{
                          padding: "6px 12px",
                          background: "#f7fafc",
                          color: "#667eea",
                          border: "1px solid #e2e8f0",
                          borderRadius: "6px",
                          fontSize: "12px",
                          fontWeight: "600",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <FaEdit /> Edit
                      </button>
                      <button
                        onClick={() => handleDeleteNote(note.id)}
                        style={{
                          padding: "6px 12px",
                          background: "#fff5f5",
                          color: "#f56565",
                          border: "1px solid #feb2b2",
                          borderRadius: "6px",
                          fontSize: "12px",
                          fontWeight: "600",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <FaTrash /> Delete
                      </button>
                    </div>
                  )}
                </div>

                {/* Content */}
                <p style={{ color: "#2d3748", fontSize: "15px", lineHeight: "1.6", margin: "0 0 12px 0", whiteSpace: "pre-wrap" }}>
                  {note.content}
                </p>

                {/* Tags */}
                {note.tags && note.tags.length > 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    <FaTag style={{ color: "#a0aec0", fontSize: "12px" }} />
                    {note.tags.map((tag, index) => (
                      <span
                        key={index}
                        style={{
                          padding: "4px 10px",
                          background: "#f7fafc",
                          border: "1px solid #e2e8f0",
                          borderRadius: "6px",
                          fontSize: "11px",
                          color: "#4a5568",
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "60px",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
              border: "1px solid #e2e8f0",
              textAlign: "center",
            }}
          >
            <FaStickyNote style={{ fontSize: "48px", color: "#cbd5e0", marginBottom: "16px" }} />
            <p style={{ color: "#718096", fontSize: "16px", marginBottom: "16px" }}>
              {selectedCommittee === "All Committees" 
                ? "No notes found. Select a specific committee to add notes."
                : `No notes yet for ${selectedCommittee}`}
            </p>
            {selectedCommittee !== "All Committees" && (
              <button
                onClick={handleAddNote}
                style={{
                  padding: "10px 20px",
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <FaPlus /> Add First Note
              </button>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Note Modal */}
      {showAddNote && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
          }}
          onClick={() => setShowAddNote(false)}
        >
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "30px",
              maxWidth: "700px",
              width: "100%",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ fontSize: "24px", fontWeight: "700", color: "#2d3748", marginBottom: "8px" }}>
              {editingNote ? "Edit Note" : "Add New Note"}
            </h2>
            <p style={{ color: "#718096", fontSize: "14px", marginBottom: "20px" }}>
              Committee: {selectedCommittee}
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: "16px", maxHeight: "calc(90vh - 200px)", overflowY: "auto", paddingRight: "8px" }}>
              {/* Template Selector */}
              <div>
                <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "600", color: "#2d3748" }}>
                  Use Template (Optional)
                </label>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {updateTypes.map(type => (
                    <button
                      key={type}
                      onClick={() => handleUseTemplate(type)}
                      style={{
                        padding: "6px 12px",
                        background: noteUpdateType === type ? "#667eea" : "#f7fafc",
                        color: noteUpdateType === type ? "white" : "#4a5568",
                        border: "1px solid",
                        borderColor: noteUpdateType === type ? "#667eea" : "#e2e8f0",
                        borderRadius: "6px",
                        fontSize: "12px",
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              {/* Update Type */}
              <div>
                <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "600", color: "#2d3748" }}>
                  Update Type *
                </label>
                <select
                  value={noteUpdateType}
                  onChange={(e) => setNoteUpdateType(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "14px",
                  }}
                >
                  {updateTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Status */}
              <div>
                <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "600", color: "#2d3748" }}>
                  Status *
                </label>
                <select
                  value={noteStatus}
                  onChange={(e) => setNoteStatus(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "14px",
                  }}
                >
                  {statusOptions.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>

              {/* Follow-up Date */}
              <div>
                <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "600", color: "#2d3748" }}>
                  Follow-up Date (Optional)
                </label>
                <input
                  type="date"
                  value={noteFollowUpDate}
                  onChange={(e) => setNoteFollowUpDate(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "14px",
                  }}
                />
              </div>

              {/* Tags */}
              <div>
                <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "600", color: "#2d3748" }}>
                  Tags (Optional)
                </label>
                <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddTag();
                      }
                    }}
                    placeholder="Add a tag..."
                    style={{
                      flex: 1,
                      padding: "8px 12px",
                      border: "1px solid #e2e8f0",
                      borderRadius: "6px",
                      fontSize: "13px",
                    }}
                  />
                  <button
                    onClick={handleAddTag}
                    style={{
                      padding: "8px 16px",
                      background: "#667eea",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      fontSize: "13px",
                      fontWeight: "600",
                      cursor: "pointer",
                    }}
                  >
                    <FaPlus />
                  </button>
                </div>
                {noteTags.length > 0 && (
                  <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                    {noteTags.map((tag, index) => (
                      <span
                        key={index}
                        style={{
                          padding: "4px 10px",
                          background: "#f7fafc",
                          border: "1px solid #e2e8f0",
                          borderRadius: "6px",
                          fontSize: "12px",
                          color: "#4a5568",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        {tag}
                        <button
                          onClick={() => handleRemoveTag(tag)}
                          style={{
                            background: "none",
                            border: "none",
                            color: "#f56565",
                            cursor: "pointer",
                            padding: "0",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <FaTimes size={10} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Note Content */}
              <div>
                <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "600", color: "#2d3748" }}>
                  Note Content *
                </label>
                <textarea
                  value={noteContent}
                  onChange={(e) => setNoteContent(e.target.value)}
                  placeholder="Enter your note here..."
                  rows={10}
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontFamily: "inherit",
                    resize: "vertical",
                  }}
                />
              </div>
            </div>

            <div style={{ display: "flex", gap: "12px", marginTop: "20px", paddingTop: "20px", borderTop: "1px solid #e2e8f0" }}>
              <button
                onClick={handleSaveNote}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "15px",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                <FaSave /> Save Note
              </button>
              <button
                onClick={() => setShowAddNote(false)}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: "#f7fafc",
                  color: "#4a5568",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  fontSize: "15px",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                <FaTimes /> Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

